/**
 * Append-only JSONL store for PerkOS chat conversations.
 *
 * Files live at:
 *   <storeRoot>/<convId>/messages.jsonl
 *   <storeRoot>/<convId>/metadata.json
 *
 * Privacy invariant: this is the canonical record of conversation content.
 * The PerkOS cloud (Firestore) holds only metadata (title, participants,
 * lastMessageAt). Bodies live exclusively here, on the agent's filesystem.
 */

import { createHash } from "node:crypto";
import { createReadStream } from "node:fs";
import {
  appendFile,
  mkdir,
  open,
  readFile,
  writeFile,
} from "node:fs/promises";
import { homedir } from "node:os";
import { join } from "node:path";

import type { ChatIdentity, ChatMessage } from "./chat-types.js";

export interface ConversationMetadata {
  convId: string;
  participants: ChatIdentity[];
  historyHost: ChatIdentity;
  /** ISO timestamp recorded the first time we see this conv. */
  createdAt: string;
  /** ISO timestamp of the most recent append. */
  updatedAt: string;
}

export interface ChatStoreOptions {
  /** Defaults to ~/.perkos/conversations */
  storeRoot?: string;
}

/** Sanitize a convId so it can safely become a directory name. */
function safeConvId(convId: string): string {
  if (!/^[A-Za-z0-9_-]{1,128}$/.test(convId)) {
    throw new Error(`invalid convId: ${convId}`);
  }
  return convId;
}

export class ChatStore {
  readonly root: string;

  constructor(options: ChatStoreOptions = {}) {
    this.root = options.storeRoot ?? join(homedir(), ".perkos", "conversations");
  }

  /** Directory for a single conversation. */
  dirFor(convId: string): string {
    return join(this.root, safeConvId(convId));
  }

  /** Path to the JSONL log for a conv. */
  jsonlPath(convId: string): string {
    return join(this.dirFor(convId), "messages.jsonl");
  }

  /** Path to the metadata file for a conv. */
  metaPath(convId: string): string {
    return join(this.dirFor(convId), "metadata.json");
  }

  /** Ensure the conv directory exists. Idempotent. */
  async ensureDir(convId: string): Promise<void> {
    await mkdir(this.dirFor(convId), { recursive: true });
  }

  /**
   * Write metadata for a conversation. Called on `channel_join` so the agent
   * has a local record of which participants belong to a conv.
   */
  async writeMetadata(meta: ConversationMetadata): Promise<void> {
    await this.ensureDir(meta.convId);
    await writeFile(this.metaPath(meta.convId), JSON.stringify(meta, null, 2));
  }

  async readMetadata(convId: string): Promise<ConversationMetadata | null> {
    try {
      const raw = await readFile(this.metaPath(convId), "utf8");
      return JSON.parse(raw) as ConversationMetadata;
    } catch (err: unknown) {
      if (isNotFound(err)) return null;
      throw err;
    }
  }

  /**
   * Append a message to the conversation log. The line written is exactly
   * `JSON.stringify(msg) + "\n"`. The store does not validate ordering; if
   * messages arrive out of timestamp order, that is recorded as-is.
   */
  async append(convId: string, msg: ChatMessage): Promise<void> {
    await this.ensureDir(convId);
    await appendFile(this.jsonlPath(convId), JSON.stringify(msg) + "\n");
  }

  /**
   * Read a history page, reverse-chronological. Returns messages with
   * timestamp strictly less than `before` (if provided), up to `limit`.
   *
   * The output order is chronological ascending — i.e. callers can `concat`
   * pages from oldest to newest without sorting.
   */
  async readPage(
    convId: string,
    opts: { before?: string | null; limit: number },
  ): Promise<{ messages: ChatMessage[]; hasMore: boolean }> {
    const limit = Math.max(1, Math.min(500, opts.limit));
    const before = opts.before ?? null;

    let lines: string[];
    try {
      const raw = await readFile(this.jsonlPath(convId), "utf8");
      lines = raw.split("\n").filter((l) => l.length > 0);
    } catch (err: unknown) {
      if (isNotFound(err)) return { messages: [], hasMore: false };
      throw err;
    }

    // Walk lines from newest (end) to oldest (start), collecting up to limit.
    const collected: ChatMessage[] = [];
    let hasMore = false;
    for (let i = lines.length - 1; i >= 0; i--) {
      let msg: ChatMessage;
      try {
        msg = JSON.parse(lines[i]) as ChatMessage;
      } catch {
        continue;
      }
      if (before && msg.timestamp >= before) continue;
      if (collected.length >= limit) {
        hasMore = true;
        break;
      }
      collected.push(msg);
    }

    return { messages: collected.reverse(), hasMore };
  }

  /**
   * Produce a tamper-evident summary of the conversation log for receipt
   * issuance. Streams `messages.jsonl` through sha256 (followed by a
   * separator + metadata.json content if present), so memory usage stays
   * constant regardless of conv size.
   *
   * The hash is deterministic: same jsonl bytes → same hex string. A
   * downstream verifier with the jsonl can recompute and confirm.
   *
   * Returns counts + first/last timestamps for the receipt manifest.
   */
  async computeReceipt(convId: string): Promise<{
    transcriptHash: string;
    hashAlgo: "sha256";
    messageCount: number;
    firstMessageAt: string | null;
    lastMessageAt: string | null;
  } | null> {
    const path = this.jsonlPath(convId);
    // Probe existence first — createReadStream() defers ENOENT to the
    // 'error' event, which would otherwise reject our Promise.
    try {
      const fd = await open(path, "r");
      await fd.close();
    } catch (err: unknown) {
      if (isNotFound(err)) return null;
      throw err;
    }
    const stream = createReadStream(path, { encoding: "utf8", highWaterMark: 64 * 1024 });
    const hash = createHash("sha256");
    let messageCount = 0;
    let firstMessageAt: string | null = null;
    let lastMessageAt: string | null = null;
    let leftover = "";

    return new Promise((resolve, reject) => {
      stream.on("data", (chunk) => {
        const text = typeof chunk === "string" ? chunk : chunk.toString("utf8");
        hash.update(text);
        const combined = leftover + text;
        const lines = combined.split("\n");
        leftover = lines.pop() ?? "";
        for (const line of lines) {
          if (!line) continue;
          messageCount++;
          try {
            const parsed = JSON.parse(line) as ChatMessage;
            const ts = parsed.timestamp;
            if (typeof ts === "string") {
              if (firstMessageAt === null || ts < firstMessageAt) firstMessageAt = ts;
              if (lastMessageAt === null || ts > lastMessageAt) lastMessageAt = ts;
            }
          } catch {
            /* malformed line — still counted toward hash */
          }
        }
      });
      stream.on("end", async () => {
        if (leftover.length > 0) {
          messageCount++;
          try {
            const parsed = JSON.parse(leftover) as ChatMessage;
            const ts = parsed.timestamp;
            if (typeof ts === "string") {
              if (firstMessageAt === null || ts < firstMessageAt) firstMessageAt = ts;
              if (lastMessageAt === null || ts > lastMessageAt) lastMessageAt = ts;
            }
          } catch { /* ignore */ }
        }
        // Fold metadata.json into the hash if present, so the receipt
        // also commits to participant + historyHost. Use a 0x1E (record
        // separator) byte as an unambiguous boundary.
        try {
          const metaRaw = await readFile(this.metaPath(convId), "utf8");
          hash.update("\x1e");
          hash.update(metaRaw);
        } catch {
          /* metadata missing → still produce a receipt over the jsonl alone */
        }
        resolve({
          transcriptHash: hash.digest("hex"),
          hashAlgo: "sha256",
          messageCount,
          firstMessageAt,
          lastMessageAt,
        });
      });
      stream.on("error", (err) => reject(err));
    });
  }

  /**
   * Return the number of messages in the log. Useful for tests and stats.
   * Avoid using this on the hot path — it reads the full file.
   */
  async count(convId: string): Promise<number> {
    let fd;
    try {
      fd = await open(this.jsonlPath(convId), "r");
    } catch (err: unknown) {
      if (isNotFound(err)) return 0;
      throw err;
    }
    try {
      const raw = await fd.readFile({ encoding: "utf8" });
      return raw.split("\n").filter((l) => l.length > 0).length;
    } finally {
      await fd.close();
    }
  }
}

function isNotFound(err: unknown): boolean {
  return !!err && typeof err === "object" && (err as { code?: string }).code === "ENOENT";
}
